import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { getCityProject } from "../lib/cityPortal";
import { ChevronRight, Check, FileText, Eye } from "lucide-react";
import { StagePill, relativeDate } from "../lib/ownerUi";
import { assetUrl, formatPriceFull, formatTypology } from "../lib/format";
import BackBar from "../components/BackBar";

const STAGE_ORDER = [
  "Selection",
  "Consultation",
  "Purchase",
  "Expedited Review",
  "Build",
  "Construction Inspection",
  "Complete",
];

export default function CityProjectReadOnly() {
  const { projectId } = useParams();
  const { user } = useRole();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    getCityProject(projectId, user.id)
      .then((d) => alive && setP(d))
      .catch((e) => alive && setErr(e));
    return () => {
      alive = false;
    };
  }, [user?.id, projectId]);

  if (err) {
    return <p style={{ color: "#A23B2E" }} data-testid="city-project-error">Could not load this project.</p>;
  }
  if (!p) {
    return <div style={{ color: "#6C6B85", fontStyle: "italic" }} data-testid="city-project-loading">Loading project…</div>;
  }

  const stageIdx = STAGE_ORDER.indexOf(p.current_stage);
  const ap = p.activation_panel || {};
  const accela = p.accela_ref || {};

  return (
    <section className="animate-fade-in-up" data-testid="city-project-readonly">
      <BackBar
        parent={{ to: "/city/cases", label: "Cases" }}
        trail={[
          { label: "City", to: "/city" },
          { label: "Cases", to: "/city/cases" },
          { label: p.design?.name || p.id },
        ]}
      />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="wn-label" style={{ color: "#8F3C23" }}>City Staff · Read-only</div>
          <h1
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(26px, 3.6vw, 32px)",
              fontWeight: 600,
              color: "#1B1F4B",
              marginTop: 6,
            }}
          >
            {p.design?.name || "Project"}
          </h1>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 14,
            }}
          >
            {p.parcel?.address || "—"} · {p.architect?.firm || "—"}
          </div>
        </div>
        <StagePill stage={p.current_stage} testId="city-project-stage" />
      </div>

      {/* Stage stepper */}
      <div className="mt-6 wn-card" style={{ padding: 16 }} data-testid="city-project-timeline">
        <div className="grid grid-cols-7 gap-2">
          {STAGE_ORDER.map((s, i) => {
            const done = i < stageIdx;
            const current = i === stageIdx;
            return (
              <div key={s} className="flex flex-col items-center text-center">
                <span
                  className="tabular-nums"
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 999,
                    background: current ? "#1B1F4B" : done ? "#5B6196" : "#FBFAFD",
                    border: "1px solid " + (current || done ? "transparent" : "#D9D8E4"),
                    color: current || done ? "#FBFAFD" : "#6C6B85",
                    fontSize: 11,
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {done ? <Check size={12} /> : i + 1}
                </span>
                <span style={{ fontSize: 10.5, color: current ? "#272636" : "#6C6B85", marginTop: 4, fontWeight: current ? 700 : 400 }}>
                  {s}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-7 grid lg:grid-cols-[2fr_1fr] gap-7">
        <div className="space-y-6">
          {/* Accela */}
          {accela.case_number && (
            <div className="wn-card" style={{ padding: 18 }}>
              <div className="wn-label">City permit (Accela)</div>
              <div
                className="tabular-nums"
                style={{ color: "#3A5563", fontWeight: 700, fontSize: 18, marginTop: 4 }}
              >
                {accela.case_number}
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: "#1B1F4B",
                  marginTop: 4,
                }}
              >
                {accela.state || "—"} · sync {accela.sync_status || "—"}
              </div>
              <Link
                to={`/city/cases/${accela.case_number}`}
                className="wn-btn wn-btn-tertiary mt-2"
                style={{ paddingLeft: 0 }}
                data-testid="city-project-view-case"
              >
                View Accela case <ChevronRight size={14} />
              </Link>
            </div>
          )}

          {/* Activation panel — read-only summary */}
          {Object.keys(ap).length > 0 && (
            <div className="wn-card" style={{ padding: 18 }} data-testid="city-project-activation">
              <h2
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 600,
                  fontSize: 18,
                  color: "#1B1F4B",
                }}
              >
                Build activations (read-only).
              </h2>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                {ap.locate_811 && (
                  <Item label="811 utility locate" value={`${ap.locate_811.ticket} · ${ap.locate_811.status}`} />
                )}
                {ap.aes && (
                  <Item label="AES electric" value={`${ap.aes.service_no} · ${ap.aes.status}`} />
                )}
                {ap.citizens_energy_gas && (
                  <Item label="Citizens Energy gas" value={`${ap.citizens_energy_gas.ref} · ${ap.citizens_energy_gas.status}`} />
                )}
                {ap.citizens_energy_water && (
                  <Item label="Citizens Energy water" value={`${ap.citizens_energy_water.ref} · ${ap.citizens_energy_water.status}`} />
                )}
                {ap.signage && (
                  <Item label="Neighborhood signage" value={`${ap.signage.ref} · ${ap.signage.status}`} />
                )}
              </div>
            </div>
          )}

          {/* Documents */}
          {(p.documents || []).length > 0 && (
            <div className="wn-card" style={{ padding: 18 }} data-testid="city-project-documents">
              <h2
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 600,
                  fontSize: 18,
                  color: "#1B1F4B",
                }}
              >
                Documents.
              </h2>
              <div className="mt-3 space-y-2">
                {(p.documents || []).map((d) => (
                  <div
                    key={d.id}
                    className="flex items-center justify-between gap-3"
                    data-testid={`city-project-doc-${d.id}`}
                  >
                    <div className="min-w-0">
                      <div
                        style={{
                          fontFamily: '"Fraunces", Georgia, serif',
                          fontWeight: 600,
                          fontSize: 13.5,
                          color: "#1B1F4B",
                        }}
                      >
                        {d.label || d.doc_type}
                      </div>
                      <div
                        style={{
                          fontFamily: '"Newsreader", Georgia, serif',
                          fontStyle: "italic",
                          color: "#6C6B85",
                          fontSize: 11.5,
                        }}
                      >
                        {relativeDate(d.generated_at)}
                      </div>
                    </div>
                    <span className="wn-btn wn-btn-tertiary" style={{ color: "#5B6196" }}>
                      <Eye size={12} /> View
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Side */}
        <aside className="space-y-5">
          {p.design && (
            <div className="wn-card" style={{ padding: 16 }}>
              <div
                style={{
                  width: "100%",
                  height: 130,
                  backgroundImage: `url(${assetUrl(p.design.exterior_photo)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  borderRadius: 4,
                }}
                aria-hidden
              />
              <div
                style={{
                  fontSize: 11.5,
                  color: "#8F3C23",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  marginTop: 8,
                }}
              >
                {formatTypology(p.design.typology)}
              </div>
              <div
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 600,
                  fontSize: 17,
                  color: "#1B1F4B",
                }}
              >
                {p.design.name}
              </div>
              <Link
                to={`/city/reviews/${p.design.id}`}
                className="wn-btn wn-btn-tertiary mt-2"
                style={{ paddingLeft: 0 }}
              >
                View design <ChevronRight size={14} />
              </Link>
            </div>
          )}

          <div className="wn-card" style={{ padding: 16 }}>
            <div className="wn-label">Costs</div>
            <dl
              className="mt-2 grid grid-cols-2 gap-y-2 gap-x-3 tabular-nums"
              style={{ fontSize: 13, color: "#272636" }}
            >
              <dt style={{ color: "#6C6B85" }}>Hard cost</dt>
              <dd>{formatPriceFull((p.costs || {}).hard_cost || 0)}</dd>
              <dt style={{ color: "#6C6B85" }}>Soft costs</dt>
              <dd>{formatPriceFull((p.costs || {}).soft_costs || 0)}</dd>
              <dt style={{ color: "#6C6B85" }}>Total</dt>
              <dd style={{ color: "#8F3C23", fontWeight: 700 }}>
                {formatPriceFull((p.costs || {}).total_cost || 0)}
              </dd>
            </dl>
          </div>
        </aside>
      </div>
    </section>
  );
}

function Item({ label, value }) {
  return (
    <div className="wn-card" style={{ padding: 12 }}>
      <div className="wn-label">{label}</div>
      <div className="tabular-nums" style={{ color: "#272636", fontSize: 12.5, marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}
